/**
 * 대시보드에 올릴 수 있는 카드 목록과, 어떤 판으로 보고 있는지를 다루는 규칙.
 *
 * **카드를 늘리는 곳이 한 곳이어야 한다.** 대시보드에 카드를 하나 더할 때마다 화면
 * 코드에 한 덩어리를 끼워 넣으면, 얼마 지나지 않아 "누구에게나 보이는 것"과 "권한이
 * 있어야 보이는 것"과 "0 이면 그리지 않는 것"이 뒤엉켜 무엇이 언제 보이는지 아무도
 * 말할 수 없게 된다. 카드의 이름·설명·권한·기본값을 여기 한 표에 두고, 화면은 이
 * 표를 읽어 그리기만 한다.
 *
 * 무엇을 어떤 크기로 어디에 둘지는 **보는 사람마다 다르다.** 현장 담당자는 꺼진 화면을
 * 크게, 광고 운영자는 재생 실적을 크게 본다. 그래서 판은 **계정별로** 서버에 남는다
 * (`/api/v1/me/dashboard`) — 한 칸을 나눠 쓰면 한 사람이 옮긴 카드가 다른 사람의
 * 대시보드를 바꾸고, 그때부터 아무도 자기 판을 믿지 못한다.
 */

/** 격자의 칸 수. 카드 하나가 차지할 수 있는 최대이기도 하다. */
export const GRID_COLUMNS = 3;

/**
 * 카드 높이(px)의 범위와 걸음 — 사람이 정한 높이(2026-10-22 사용자 지시 "대시보드에 편집을 하면 각 카드별로 높이 조정이
 * 안된다"). 정하지 않으면 내용 높이다. 걸음으로 맞추는 것은 이웃 카드와 끝을 맞추기 쉽게 하려는 것이고, 아래 끝은 머리와
 * 한두 줄이 보일 높이, 위 끝은 화면 두 장쯤이다. 서버도 같은 범위로 막는다(`/api/v1/me/dashboard`).
 */
export const CARD_HEIGHT_MIN = 120;
export const CARD_HEIGHT_MAX = 1600;
export const CARD_HEIGHT_STEP = 20;

export interface DashboardCard {
  id: string;
  /** 편집 목록과 카드 머리에 쓰는 이름. */
  title: string;
  /** 이 카드가 무엇에 답하는지 한 줄. 고르는 사람이 읽는다. */
  hint: string;
  /**
   * 이 능력이 없으면 **목록에도 나오지 않는다.**
   *
   * 감추는 것은 편의이지 방어가 아니다(서버가 같은 권한을 다시 검사한다). 다만 고를 수
   * 없는 카드를 늘어놓으면, 골라 놓고 빈 카드를 보거나 권한 오류를 읽게 된다.
   */
  capability?: string;
  /** 처음 올라올 때의 칸 수. 표·목록은 넓어야 읽히고, 도넛 하나는 한 칸이면 된다. */
  defaultSpan: number;
  /** 처음 여는 사람에게 올라와 있는가. */
  fallback: boolean;
}

/** 판에 놓인 카드 하나 — 어떤 카드가 몇 칸을 차지하는가. 순서는 배열이 가진다. */
export interface CardPlacement {
  id: string;
  span: number;
  /** 사람이 정한 높이(px). 없으면(`null`·옛 판) 내용 높이다. */
  height?: number | null;
}

/**
 * 고를 수 있는 카드 — 배열 순서는 **편집 목록의 순서**이자 처음 판의 순서다.
 *
 * 운영자가 아침에 묻는 순서를 따른다: 지금 문제가 있나(요약) → 오늘 잘 나가고 있나 →
 * 어디가 문제인가(위치·화면) → 무엇이 있나(콘텐츠) → 누가 바꿨나.
 */
export const DASHBOARD_CARDS: readonly DashboardCard[] = [
  {
    id: 'summary',
    title: '운영 요약',
    hint: '온라인 화면·콘텐츠·편성·주의 필요 수',
    defaultSpan: 3,
    fallback: true,
  },
  {
    id: 'playToday',
    title: '오늘 재생 실적',
    hint: '오늘 재생 횟수·어제 대비·증빙 끊김 경보',
    capability: 'report:read',
    defaultSpan: 3,
    fallback: false,
  },
  {
    id: 'regions',
    title: '위치별 화면 현황',
    hint: '위치별 온라인·오프라인 대수 (위치 2곳 이상)',
    defaultSpan: 3,
    fallback: true,
  },
  {
    id: 'displays',
    title: '화면 현황',
    hint: '문제 화면 우선 8대 (오류·오프라인·미배정 순)',
    defaultSpan: 2,
    fallback: true,
  },
  /*
   * 곧 게시가 끝나는 콘텐츠 — 광고 모델 계약·행사가 끝나는 날을 미리 본다. 끝난 뒤에는 화면이
   * 스스로 빼지만, 대신 틀 것을 준비할 시간은 미리 봐야 생긴다.
   */
  {
    id: 'expiring',
    title: '곧 끝나는 콘텐츠',
    hint: '7일 안에 게시가 끝나는 콘텐츠',
    defaultSpan: 1,
    fallback: true,
  },
  {
    id: 'contentTypes',
    title: '콘텐츠 유형',
    hint: '이미지·영상·웹·템플릿별 개수',
    defaultSpan: 1,
    fallback: true,
  },
  /*
   * 공개된 기능 — **공급사가 고객사에 넘긴 선택 기능**만 여기 나온다(`/api/v1/plugins/modules`).
   *
   * 무엇을 공개할지는 개발자가 정비 모드 ▸ 플러그인에서 정하고, 공개하지 않은 것은 이 카드에
   * 아예 오지 않는다. 처음 판에는 올라와 있지 않다(`fallback: false`) — 공개한 것이 없는 현장이
   * 대부분이라 한 칸을 미리 내줄 이유가 없다.
   *
   * **고를 수 있는 목록에는 늘 있다**(위의 '위치별 화면 현황' 과 다르다 — 그쪽은 조건이 맞을 때만
   * 그려진다). 공개한 것이 없으면 카드는 "공개된 선택 기능이 없습니다" 한 줄이 된다. 고른 카드가
   * 아무 말 없이 사라지면 고객사는 자기가 잘못 골랐다고 읽는다.
   */
  {
    id: 'modules',
    title: '공개된 기능',
    hint: '공개된 선택 기능의 상태·최근 결과',
    defaultSpan: 1,
    fallback: false,
  },
  {
    id: 'activity',
    title: '최근 활동',
    hint: '최근 변경 8건',
    capability: 'audit:read',
    defaultSpan: 3,
    fallback: true,
  },
];

/**
 * 판을 이 브라우저에도 남겨 두는 자리.
 *
 * 저장소는 서버다. 이것은 **첫 그림을 위한 사본**이다 — 서버 응답을 기다리는 동안 기본
 * 판을 그렸다가 바꾸면, 새로고침할 때마다 카드들이 한 번 튀었다 제자리를 찾는다.
 *
 * **열쇠에 계정을 붙인다.** 한 열쇠를 함께 쓰던 때, 공용 PC 에서 앞사람의 판이 다음
 * 사람에게 그대로 보였다 — 서버가 "이 계정은 정한 적 없음(`cards: null`)" 이라고 답해도
 * 콘솔은 브라우저 사본을 그대로 두기 때문이다. 판 자체는 계정에 저장되고 있었는데,
 * 화면만 남의 것을 그리고 있었다.
 */
const DASHBOARD_CARDS_PREFIX = 'signageone.dashboard.cards';

/** 계정을 함께 쓰지 않는 한 사람의 열쇠. `who` 는 계정 id(없으면 주체 id)다. */
export function dashboardCardsKey(who: string): string {
  return `${DASHBOARD_CARDS_PREFIX}:${who}`;
}

/**
 * 계정을 가리지 않던 옛 열쇠. 남의 판이 들어 있을 수 있어 **읽지 않고 지운다.**
 * 잃는 것은 첫 그림의 매끄러움 한 번뿐이고, 판은 계정(서버)에 있다.
 */
export const LEGACY_DASHBOARD_CARDS_KEY = DASHBOARD_CARDS_PREFIX;

export function cardById(id: string): DashboardCard | null {
  return DASHBOARD_CARDS.find((card) => card.id === id) ?? null;
}

/** 이 사람이 고를 수 있는 카드. */
export function availableCards(capabilities: readonly string[]): DashboardCard[] {
  return DASHBOARD_CARDS.filter((card) => !card.capability || capabilities.includes(card.capability));
}

/** 격자 밖으로 나가지 않게. */
export function clampSpan(span: number): number {
  if (!Number.isFinite(span)) return 1;
  return Math.max(1, Math.min(GRID_COLUMNS, Math.round(span)));
}

/** 끌어 간 높이를 걸음에 맞추고 범위 안으로. */
export function clampHeight(height: number): number {
  if (!Number.isFinite(height)) return CARD_HEIGHT_MIN;
  const stepped = Math.round(height / CARD_HEIGHT_STEP) * CARD_HEIGHT_STEP;
  return Math.max(CARD_HEIGHT_MIN, Math.min(CARD_HEIGHT_MAX, stepped));
}

/** 저장된 높이를 읽는다 — 숫자가 아니거나 없으면 내용 높이(`null`). */
function readHeight(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? clampHeight(value) : null;
}

/**
 * 처음 여는 사람이 보는 판.
 *
 * **빈 대시보드로 시작하지 않는다.** 고르는 화면을 만들었다고 해서 처음 온 사람에게
 * 아무것도 없는 판을 내밀면, 이 서비스가 무엇을 보여줄 수 있는지조차 알 수 없다.
 */
export function defaultLayout(capabilities: readonly string[]): CardPlacement[] {
  return availableCards(capabilities)
    .filter((card) => card.fallback)
    .map((card) => ({ id: card.id, span: card.defaultSpan }));
}

/**
 * 받아 온 판을 쓸 수 있는 꼴로 — 서버에서 왔든 이 브라우저에서 왔든 같은 규칙이다.
 *
 * 모르는 카드는 버린다. 카드를 없애거나 이름을 바꾼 뒤에도 옛 판이 그대로 남아 있어,
 * 그것을 믿고 그리면 화면이 깨진다. 권한이 없어진 카드도, 두 번 적힌 카드도 버린다.
 */
export function normalizeLayout(
  cards: readonly unknown[],
  capabilities: readonly string[],
): CardPlacement[] {
  const allowed = new Set(availableCards(capabilities).map((card) => card.id));
  const seen = new Set<string>();
  const layout: CardPlacement[] = [];

  for (const entry of cards) {
    // 옛 판은 id 문자열만 늘어놓았다 — 칸 수가 생기기 전에 저장된 것을 버리지 않는다.
    const placement =
      typeof entry === 'string'
        ? { id: entry, span: cardById(entry)?.defaultSpan ?? 1 }
        : typeof entry === 'object' && entry !== null && typeof (entry as CardPlacement).id === 'string'
          ? {
              id: (entry as CardPlacement).id,
              span: (entry as CardPlacement).span,
              height: readHeight((entry as CardPlacement).height),
            }
          : null;

    if (!placement || !allowed.has(placement.id) || seen.has(placement.id)) continue;
    seen.add(placement.id);
    layout.push({ id: placement.id, span: clampSpan(placement.span), height: placement.height ?? null });
  }

  return layout;
}

/**
 * 이 브라우저에 남겨 둔 판을 읽는다.
 *
 * 저장된 적이 없으면(`null`) 기본 판이다. **빈 배열은 기본 판이 아니다** — 카드를 전부
 * 내린 사람의 선택이고, 그것을 기본으로 되돌리면 내릴 수 없는 대시보드가 된다.
 */
export function readLayout(raw: string | null, capabilities: readonly string[]): CardPlacement[] {
  if (raw === null) return defaultLayout(capabilities);

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaultLayout(capabilities);
  }
  if (!Array.isArray(parsed)) return defaultLayout(capabilities);

  return normalizeLayout(parsed, capabilities);
}

/**
 * 카드를 올리고 내린다.
 *
 * 올릴 때는 **판의 맨 뒤**에 붙인다. 표의 자리로 끼워 넣으면, 껐다 켜는 것만으로 카드가
 * 이미 자리를 잡아 둔 판 한가운데로 뛰어든다 — 자리는 사람이 정한 것이고 그것을 흔들지
 * 않는다.
 */
export function toggleCard(
  layout: readonly CardPlacement[],
  id: string,
  capabilities: readonly string[],
): CardPlacement[] {
  if (layout.some((card) => card.id === id)) return layout.filter((card) => card.id !== id);

  const card = availableCards(capabilities).find((entry) => entry.id === id);
  if (!card) return [...layout];
  return [...layout, { id: card.id, span: card.defaultSpan, height: null }];
}

/**
 * 한 카드의 칸 수(와 높이)를 바꾼다. 격자 밖은 없다. `height` 를 주지 않으면 높이는 그대로, `null` 이면 내용 높이로.
 */
export function resizeCard(
  layout: readonly CardPlacement[],
  id: string,
  span: number,
  height?: number | null,
): CardPlacement[] {
  return layout.map((card) =>
    card.id === id
      ? {
          ...card,
          span: clampSpan(span),
          height: height === undefined ? (card.height ?? null) : height === null ? null : clampHeight(height),
        }
      : card,
  );
}

/** 두 판이 같은가 — 서버에서 온 것과 지금 보고 있는 것을 견줄 때. 높이가 없는 것(옛 판)과 `null` 은 같다. */
export function sameLayout(a: readonly CardPlacement[], b: readonly CardPlacement[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (card, at) =>
        card.id === b[at]?.id && card.span === b[at]?.span && (card.height ?? null) === (b[at]?.height ?? null),
    )
  );
}
